// TrackFiji — small geometry helpers (V3.3.0): distances, circles, zone shapes, coordinate formats.
import type { Zone } from "./org";

export type LngLat = [number, number];

/** Great-circle distance in metres. */
export function metres([aLng, aLat]: LngLat, [bLng, bLat]: LngLat) {
  const r = Math.PI / 180, R = 6371008.8;
  const h = Math.sin((bLat - aLat) * r / 2) ** 2 + Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin((bLng - aLng) * r / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function pathMetres(pts: LngLat[]) {
  let m = 0;
  for (let i = 1; i < pts.length; i++) m += metres(pts[i - 1], pts[i]);
  return m;
}

export function fmtDistance(m: number) {
  if (m < 1000) return `${Math.round(m)} m`;
  if (m < 100_000) return `${(m / 1000).toFixed(2)} km`;
  return `${Math.round(m / 1000)} km`;
}

/** Approximate polygon area in m² (equirectangular projection — fine for zone-sized shapes). */
export function areaM2(pts: LngLat[]) {
  if (pts.length < 3) return 0;
  const lat0 = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const kx = 111320 * Math.cos((lat0 * Math.PI) / 180), ky = 110540;
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += (pts[j][0] * kx) * (pts[i][1] * ky) - (pts[i][0] * kx) * (pts[j][1] * ky);
  return Math.abs(a / 2);
}
export function fmtArea(m2: number) {
  return m2 >= 1_000_000 ? `${(m2 / 1_000_000).toFixed(2)} km²` : m2 >= 10_000 ? `${(m2 / 10_000).toFixed(1)} ha` : `${Math.round(m2)} m²`;
}

export function circle([lng, lat]: LngLat, radiusM: number) {
  const pts: LngLat[] = [];
  const dLat = radiusM / 111320, dLng = radiusM / (111320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= 64; i++) { const a = (i / 64) * 2 * Math.PI; pts.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]); }
  return { type: "Feature" as const, properties: {} as Record<string, unknown>, geometry: { type: "Polygon" as const, coordinates: [pts] } };
}

/** GeoJSON feature for a stored zone (circle or polygon). */
export function zoneFeature(z: Pick<Zone, "kind" | "center_lat" | "center_lng" | "radius_m" | "polygon">) {
  if (z.kind === "polygon" && z.polygon && z.polygon.length >= 3) {
    const ring = [...z.polygon, z.polygon[0]];
    return { type: "Feature" as const, properties: {} as Record<string, unknown>, geometry: { type: "Polygon" as const, coordinates: [ring] } };
  }
  return circle([z.center_lng, z.center_lat], z.radius_m ?? 100);
}

export function zoneSize(z: Pick<Zone, "kind" | "radius_m" | "polygon">) {
  if (z.kind === "polygon") return `Polygon · ${fmtArea(areaM2(z.polygon ?? []))}`;
  const r = z.radius_m ?? 0;
  return `Circle · ${r >= 1000 ? `${(r / 1000).toFixed(1)} km` : `${r} m`} radius`;
}

/** 17°46'35.2"S 177°26'08.2"E */
export function dms(lat: number, lng: number) {
  const f = (v: number, pos: string, neg: string) => {
    const a = Math.abs(v), d = Math.floor(a), mf = (a - d) * 60, m = Math.floor(mf), s = (mf - m) * 60;
    return `${d}°${String(m).padStart(2, "0")}'${s.toFixed(1).padStart(4, "0")}"${v >= 0 ? pos : neg}`;
  };
  return `${f(lat, "N", "S")} ${f(lng, "E", "W")}`;
}

export const googleMapsLink = (lat: number, lng: number) =>
  `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lng.toFixed(6)}`;

// ---------------------------------------------------------------- nested zones (V3.4.0)
/** Point-in-zone test (circle by distance, polygon by ray casting). */
export function inZone(z: Pick<Zone, "kind" | "center_lat" | "center_lng" | "radius_m" | "polygon">, lat: number, lng: number) {
  if (z.kind === "polygon" && z.polygon && z.polygon.length >= 3) {
    const p = z.polygon; let inside = false;
    for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
      const [xi, yi] = p[i], [xj, yj] = p[j];
      if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }
  return metres([lng, lat], [z.center_lng, z.center_lat]) <= (z.radius_m ?? 0);
}
export const zoneArea = (z: Pick<Zone, "kind" | "radius_m" | "polygon">) =>
  z.kind === "polygon" ? areaM2(z.polygon ?? []) : Math.PI * (z.radius_m ?? 0) ** 2;

/** Every zone containing the point, innermost (smallest area) first. */
export function zonesAt<T extends Zone>(zones: T[], lat: number | null | undefined, lng: number | null | undefined): T[] {
  if (lat == null || lng == null) return [];
  return zones.filter((z) => inZone(z, lat, lng)).sort((a, b) => zoneArea(a) - zoneArea(b));
}
/** "Nadi Airport › Nadi Town" — innermost first. */
export const zoneStack = (zs: Pick<Zone, "name">[], max = 3) =>
  zs.length ? zs.slice(0, max).map((z) => z.name).join(" › ") + (zs.length > max ? ` (+${zs.length - max})` : "") : "";
